import { toast } from 'sonner'

import { queryClient } from '@/app/queryClient'
import i18n from '@/locales/i18n'
import { useAuthStore } from '@/stores/auth.store'

import { authStorage } from './auth-storage'

// SPA-navigate to the login page, keeping the current location as `redirect` so
// the user lands back where they were after re-authenticating. The router is
// imported lazily to avoid an eager import cycle (router → pages → api → http →
// here) and because this only ever runs at request time, long after startup.
// Falls back to a hard navigation if the SPA router is unavailable.
const redirectToLogin = async (): Promise<void> => {
  const { pathname, search } = window.location
  if (pathname.startsWith('/login')) return

  const redirect = encodeURIComponent(`${pathname}${search}`)
  const loginUrl = `/login?redirect=${redirect}`

  try {
    const { router } = await import('@/app/router')
    await router.navigate(loginUrl, { replace: true })
  } catch {
    window.location.replace(loginUrl)
  }
}

/**
 * Centralized "session is no longer valid" handling, used both when a 401 can't
 * be refreshed and when proactive refresh fails. Clears auth state + query
 * cache, tells the user, and routes to login via the SPA router (no full page
 * reload when possible, so in-progress workbench state in Zustand can survive).
 *
 * Concurrent callers are de-duplicated: the first call clears storage + store,
 * so later calls see no token and bail before re-toasting / re-navigating.
 */
export const handleSessionExpired = (): void => {
  const { accessToken, logout } = useAuthStore.getState()
  // Also check persistent storage: some 401 paths may clear the in-memory token
  // before this runs, and we still need to finish the logout UX once.
  const hasSession = Boolean(accessToken || authStorage.getAccess())
  if (!hasSession) return

  logout()
  // Drop cached auth/me (and everything else) so AuthInit can't re-hydrate a
  // logged-out session from a stale React Query result.
  queryClient.clear()
  toast.error(i18n.t('auth:session_expired'))
  void redirectToLogin()
}
